import { FiPlus } from '@react-icons/all-files/fi/FiPlus'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useRef, useState } from 'react'
import { CopySnippet } from '~/components/common/CopySnippet'
import { ServerIcon } from '~/components/icons'
import { InputArea } from '~/components/common/InputArea'
import { ProgressBar } from '~/components/common/progress-bar/ProgressBar'
import { Selector } from '~/components/common/Selector'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import {
  createRunner,
  getOperatingSystems,
  getRunners,
  postRunnerInstallSetup,
  updateRunner,
  type CreateRunnerRequest,
  type InstallRunnerResponse,
  type Runner as RunnerApi
} from '~/services/runnerService'
import { BetterToast } from '~/utils/toast'
import { Button } from '~/components/common/Button'
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeaderContent,
  useModal
} from '~/components/modals/CurieModal'
import { RunnersList, type Runner as RunnerType } from '~/components/plugin/RunnersList'

// Modal types
type ModalMode = 'create' | 'edit' | 'configure'

// Handler types for the component
export interface RunnerHandlers {
  onCreateRunner: (runnerData: CreateRunnerRequest) => Promise<{ id: number; uuid: string }>
  onUpdateRunner: (runnerId: number, runnerData: Partial<CreateRunnerRequest>) => Promise<void>
}

const CreateEditRunnerComponent = ({
  runnerName,
  setRunnerName,
  setOs,
  setArch,
  osLabel,
  setOsLabel,
  osOptions
}: {
  runnerName: string
  setRunnerName: (name: string) => void
  setOs: (os: string | undefined) => void
  setArch: (arch: string | undefined) => void
  osLabel: string | undefined
  setOsLabel: (label: string | undefined) => void
  osOptions: { name: string; os: string; arch: string }[]
}) => {
  return (
    <div className='space-y-10'>
      <div>
        <label className='block text-sm font-medium text-tokens-neutral-text-secondary mb-2 tracking-wide select-none'>
          Runner Name<span className='text-tokens-status-error-1'>*</span>
        </label>
        <div className='flex items-center justify-center gap-1 mb-2'>
          <InputArea
            placeholder='e.g., Production-1-Runner'
            value={runnerName}
            onChange={e => setRunnerName(e.target.value)}
            fullWidth
            singleLine
            aria-label='Runner name input'
          />
        </div>
      </div>
      <div>
        <label className='block text-sm font-medium text-tokens-neutral-text-secondary mb-2 tracking-wide select-none'>
          Operation System<span className='text-tokens-status-error-1'>*</span>
        </label>
        <Selector
          options={osOptions.map(opt => ({
            key: opt.name,
            value: opt.name,
            label: opt.name
          }))}
          value={osLabel}
          onSelectionChange={val => {
            const found = osOptions.find(opt => opt.name === val)
            if (!found) return
            setOs(found.os)
            setArch(found.arch)
            setOsLabel(found.name)
          }}
          placeholder='Select OS'
          className='w-full'
        />
      </div>
    </div>
  )
}

export const ConfigurationComponent = ({
  installCommand,
  isLoadingInstallCommand
}: {
  installCommand: InstallRunnerResponse | null
  isLoadingInstallCommand: boolean
}) => {
  const isWindows = installCommand?.os === 'windows'
  const displayCommand =
    isWindows && installCommand?.cmd_windows ? installCommand.cmd_windows : installCommand?.cmd

  return (
    <div className='flex flex-col gap-3'>
      {isWindows ? (
        <>
          <p className='font-mono font-medium text-sm text-tokens-neutral-text-secondary select-none'>
            Run this command in PowerShell or Command Prompt
          </p>
          <div className='bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2 select-none'>
            <p className='text-sm text-amber-800 font-medium mb-1'>Prerequisites for Windows:</p>
            <ul className='text-xs text-amber-700 list-disc list-inside space-y-1'>
              <li>
                WSL 2 must be installed (
                <code className='bg-amber-100 px-1 rounded'>wsl --install</code>)
              </li>
              <li>A Linux distribution (Debian) in WSL</li>
              <li>
                Required packages in WSL:{' '}
                <code className='bg-amber-100 px-1 rounded'>sudo apt install -y curl jq patch</code>
              </li>
            </ul>
          </div>
        </>
      ) : (
        <p className='font-mono font-medium text-sm text-tokens-neutral-text-secondary select-none'>
          Paste and execute the command in Terminal
        </p>
      )}
      {isLoadingInstallCommand ? (
        <div className='bg-tokens-neutral-bg-grey-2 rounded-xl p-3 w-full'>
          <p className='font-mono text-sm text-tokens-neutral-text-tertiary animate-pulse select-none'>
            Fetching install command...
          </p>
        </div>
      ) : !installCommand ? (
        <div className='bg-tokens-neutral-bg-grey-2 rounded-xl p-3 w-full'>
          <p className='font-mono text-sm text-tokens-status-error-1 select-none'>
            Error loading command
          </p>
        </div>
      ) : (
        <CopySnippet text='' code={displayCommand || ''} />
      )}
    </div>
  )
}

const ConfigurationSvgIcon: React.FC = () => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M8.99817 2H14.9982V4.58433C15.0393 4.60084 15.0803 4.6177 15.1211 4.63489L16.9482 2.80779L21.1904 7.05L19.3633 8.8771C19.3805 8.91792 19.3973 8.95888 19.4138 9H21.9982V15H19.4144C19.398 15.041 19.3812 15.0819 19.364 15.1227L21.1915 16.9501L16.948 21.1921L15.1211 19.3651C15.0803 19.3823 15.0393 19.3992 14.9982 19.4157V22H8.99817V19.4157C8.95705 19.3992 8.91609 19.3823 8.87527 19.3651L7.04817 21.1922L2.80596 16.95L4.63306 15.1229C4.61587 15.0821 4.59901 15.0411 4.5825 15H1.99817V9H4.5825C4.59901 8.95888 4.61587 8.91792 4.63306 8.8771L2.80596 7.05L7.04817 2.80779L8.87527 4.63489C8.91609 4.6177 8.95705 4.60084 8.99817 4.58433V2ZM10.9982 4V6.04033L10.2827 6.25267C9.88157 6.37171 9.50013 6.5286 9.14452 6.72126L8.48858 7.07662L7.04817 5.63621L5.63438 7.05L7.07479 8.49041L6.71942 9.14635C6.52677 9.50196 6.36988 9.8834 6.25084 10.2845L6.0385 11H3.99817V13H6.0385L6.25084 13.7155C6.36988 14.1166 6.52677 14.498 6.71942 14.8536L7.07479 15.5096L5.63438 16.95L7.04817 18.3638L8.48858 16.9234L9.14452 17.2787C9.50013 17.4714 9.88157 17.6283 10.2827 17.7473L10.9982 17.9597V20H12.9982V17.9597L13.7137 17.7473C14.1148 17.6283 14.4962 17.4714 14.8518 17.2787L15.5078 16.9234L16.9483 18.3639L18.3628 16.9499L16.9225 15.5096L17.2779 14.8536C17.47 14.4991 17.6264 14.1173 17.7471 13.7136L17.9604 13H19.9982V11H17.9578L17.7455 10.2845C17.6265 9.8834 17.4696 9.50196 17.2769 9.14635L16.9215 8.49041L18.362 7.05L16.9482 5.63621L15.5078 7.07662L14.8518 6.72126C14.4962 6.5286 14.1148 6.37171 13.7137 6.25267L12.9982 6.04033V4H10.9982ZM11.9982 11C11.4459 11 10.9982 11.4477 10.9982 12C10.9982 12.5523 11.4459 13 11.9982 13C12.5505 13 12.9982 12.5523 12.9982 12C12.9982 11.4477 12.5505 11 11.9982 11ZM8.99817 12C8.99817 10.3431 10.3413 9 11.9982 9C13.655 9 14.9982 10.3431 14.9982 12C14.9982 13.6569 13.655 15 11.9982 15C10.3413 15 8.99817 13.6569 8.99817 12Z'
      fill='#161513'
    />
  </svg>
)

export const RunnerSettingsTab: React.FC = () => {
  const { currentWorkspace } = useWorkspaceContext()
  const queryClient = useQueryClient()
  const runnerModal = useModal()
  const [modalMode, setModalMode] = useState<ModalMode>('create')
  const [currentStep, setCurrentStep] = useState(1)
  const [editingRunner, setEditingRunner] = useState<RunnerApi | null>(null)
  const [installCommand, setInstallCommand] = useState<InstallRunnerResponse | null>(null)
  const [isLoadingInstallCommand, setIsLoadingInstallCommand] = useState(false)
  const currentRunnerData = useRef<CreateRunnerRequest | null>(null)

  // Form State
  const [runnerName, setRunnerName] = useState('')
  const [os, setOs] = useState<string | undefined>()
  const [arch, setArch] = useState<string | undefined>()
  const [osLabel, setOsLabel] = useState<string | undefined>()

  const { data: osOptions = [], isLoading: isLoadingOsOptions } = useQuery({
    queryKey: ['operating-systems', currentWorkspace?.uuid],
    queryFn: () => {
      if (!currentWorkspace?.uuid) throw new Error('Workspace not found')
      return getOperatingSystems({ workspaceUuid: currentWorkspace.uuid })
    },
    enabled: !!currentWorkspace?.uuid
  })

  const {
    data: apiRunners = [],
    isLoading: isLoadingRunners,
    error
  } = useQuery({
    queryKey: ['runners', currentWorkspace?.uuid],
    queryFn: () => {
      if (!currentWorkspace?.uuid) throw new Error('Workspace not found')
      return getRunners({ workspaceUuid: currentWorkspace.uuid })
    },
    enabled: !!currentWorkspace?.uuid
  })

  useEffect(() => {
    if (!runnerModal.isOpen) return

    const runnerToEdit = editingRunner
    if (modalMode === 'edit' && runnerToEdit) {
      setRunnerName(runnerToEdit.name)
      setOs(runnerToEdit.os)
      setArch(runnerToEdit.arch)
      const foundOs = osOptions.find(o => o.os === runnerToEdit.os && o.arch === runnerToEdit.arch)
      setOsLabel(foundOs?.name)
    } else {
      setRunnerName('')
      if (osOptions.length > 0) {
        setOs(osOptions[0].os)
        setArch(osOptions[0].arch)
        setOsLabel(osOptions[0].name)
      } else {
        setOs(undefined)
        setArch(undefined)
        setOsLabel(undefined)
      }
    }
  }, [runnerModal.isOpen, editingRunner, modalMode, osOptions])

  // Handler functions
  const handlers: RunnerHandlers = {
    onCreateRunner: async (runnerData: CreateRunnerRequest) => {
      if (!currentWorkspace?.uuid) throw new Error('Workspace not found')
      const result = await createRunner(currentWorkspace.uuid, runnerData)
      queryClient.invalidateQueries({ queryKey: ['runners'] })
      return result
    },
    onUpdateRunner: async (runnerId: number, runnerData: Partial<CreateRunnerRequest>) => {
      if (!currentWorkspace?.uuid) throw new Error('Workspace not found')
      await updateRunner(currentWorkspace.uuid, runnerId, runnerData)
      queryClient.invalidateQueries({ queryKey: ['runners'] })
    }
  }

  // Handle create runner
  const handleCreateRunner = async (runnerData: CreateRunnerRequest) => {
    try {
      if (!currentWorkspace?.uuid) {
        BetterToast.error('Workspace not found')
        return
      }
      const data = await handlers.onCreateRunner(runnerData)
      setIsLoadingInstallCommand(true)
      setInstallCommand(null)
      try {
        const command = await postRunnerInstallSetup(currentWorkspace.uuid, data.id)
        setInstallCommand(command)
      } catch (error) {
        console.error('Failed to get install command:', error)
        BetterToast.error('Failed to get install command')
      } finally {
        setIsLoadingInstallCommand(false)
      }

      setCurrentStep(2)
    } catch (error) {
      BetterToast.error(
        `Failed to create runner: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  // Handle update runner
  const handleUpdateRunner = async (runnerId: number, runnerData: Partial<CreateRunnerRequest>) => {
    try {
      await handlers.onUpdateRunner(runnerId, runnerData)
      handleCloseModal()
    } catch (error) {
      BetterToast.error(
        `Failed to update runner: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  const handleAddRunner = () => {
    setModalMode('create')
    setCurrentStep(1)
    setEditingRunner(null)
    setInstallCommand(null)
    setIsLoadingInstallCommand(false)
    currentRunnerData.current = null
    runnerModal.open()
  }

  const handleEditRunner = (runner: RunnerType) => {
    setModalMode('edit')
    setEditingRunner(runner)
    setCurrentStep(1)
    setInstallCommand(null)
    setIsLoadingInstallCommand(false)
    currentRunnerData.current = null
    runnerModal.open()
  }

  const handleConfigureRunner = async (runner: RunnerType) => {
    setModalMode('configure')
    setCurrentStep(1)
    setEditingRunner(null)
    currentRunnerData.current = null
    runnerModal.open()

    setIsLoadingInstallCommand(true)
    setInstallCommand(null)
    try {
      if (!currentWorkspace?.uuid) {
        throw new Error('Workspace not found')
      }
      const command = await postRunnerInstallSetup(currentWorkspace.uuid, runner.id)
      setInstallCommand(command)
    } catch (error) {
      console.error('Failed to get install command:', error)
      BetterToast.error('Failed to get install command')
    } finally {
      setIsLoadingInstallCommand(false)
    }
  }

  const handleCloseModal = () => {
    runnerModal.close()
    setTimeout(() => {
      setCurrentStep(1)
      setEditingRunner(null)
      setInstallCommand(null)
      setIsLoadingInstallCommand(false)
      currentRunnerData.current = null
    }, 300)
  }

  const handleNext = (name: string, os: string, arch: string) => {
    const runnerData: CreateRunnerRequest = { name, os, arch }
    currentRunnerData.current = runnerData

    if (modalMode === 'edit' && editingRunner) {
      handleUpdateRunner(editingRunner.id, runnerData)
    } else {
      handleCreateRunner(runnerData)
    }
  }

  const handleNextClick = () => {
    if (!runnerName.trim()) {
      BetterToast.error('Please enter a runner name')
      return
    }
    handleNext(runnerName, os || '', arch || '')
  }

  const handleComplete = () => {
    handleCloseModal()
  }

  if (isLoadingRunners || isLoadingOsOptions) {
    return <div className='select-none'>Loading runners...</div>
  }

  if (error) {
    return <div>Error loading runners: {error.message}</div>
  }

  const runners: RunnerType[] = apiRunners.map(runner => ({
    id: runner.id,
    name: runner.name,
    uuid: runner.uuid,
    org_id: runner.org_id,
    active: runner.active,
    os: runner.os,
    arch: runner.arch
  }))

  const hasRunners = runners.length > 0

  return (
    <div className='w-full h-full overflow-auto no-scrollbar'>
      {hasRunners ? (
        <RunnersList
          runners={runners}
          onAddRunner={handleAddRunner}
          onEditRunner={handleEditRunner}
          onConfigureRunner={handleConfigureRunner}
        />
      ) : (
        <>
          <div className='flex flex-col items-center justify-center py-16'>
            <div className='w-24 h-24 flex items-center justify-center mb-6'>
              <svg
                width='96'
                height='96'
                viewBox='0 0 96 96'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M80 8H16C11.5817 8 8 11.5817 8 16V32C8 36.4183 11.5817 40 16 40H80C84.4183 40 88 36.4183 88 32V16C88 11.5817 84.4183 8 80 8Z'
                  stroke='#262626'
                  strokeOpacity='0.2'
                  strokeWidth='8'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M80 56H16C11.5817 56 8 59.5817 8 64V80C8 84.4183 11.5817 88 16 88H80C84.4183 88 88 84.4183 88 80V64C88 59.5817 84.4183 56 80 56Z'
                  stroke='#262626'
                  strokeOpacity='0.2'
                  strokeWidth='8'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M24 24H24.04'
                  stroke='#262626'
                  strokeOpacity='0.2'
                  strokeWidth='8'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M24 72H24.04'
                  stroke='#262626'
                  strokeOpacity='0.2'
                  strokeWidth='8'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
            <p className='text-tokens-neutral-text-tertiary mb-6 text-center max-w-md text-base select-none'>
              Register a private runner <br />
              to execute tasks inside your secure environment.
            </p>
            <Button
              onClick={handleAddRunner}
              variant='outlined'
              leftIcon={<FiPlus className='w-4 h-4' />}
              aria-label='Add runner button'
              className='select-none'
            >
              Add Runner
            </Button>
          </div>
        </>
      )}

      <Modal isOpen={runnerModal.isOpen} onClose={handleCloseModal} hideCloseButton>
        <ModalBody>
          {modalMode === 'create' && <ProgressBar currentStep={currentStep} totalSteps={2} />}
          <div
            style={{
              marginTop: modalMode === 'create' ? 60 : 'calc(60px - 24px)'
            }}
          >
            <ModalHeaderContent
              icon={
                modalMode === 'create' || modalMode === 'edit' ? (
                  <ServerIcon />
                ) : (
                  <ConfigurationSvgIcon />
                )
              }
              title={
                modalMode === 'create'
                  ? currentStep === 1
                    ? 'Create Runner'
                    : 'Configuration'
                  : modalMode === 'edit'
                    ? 'Edit Runner'
                    : 'Configuration'
              }
            />
          </div>
          {currentStep === 1 && (modalMode === 'create' || modalMode === 'edit') && (
            <div className='w-[336px] mx-auto mt-[48px]'>
              <CreateEditRunnerComponent
                runnerName={runnerName}
                setRunnerName={setRunnerName}
                setOs={setOs}
                setArch={setArch}
                osLabel={osLabel}
                setOsLabel={setOsLabel}
                osOptions={osOptions}
              />
            </div>
          )}
          {((modalMode === 'create' && currentStep === 2) || modalMode === 'configure') && (
            <div className='mx-auto mt-[48px]'>
              <ConfigurationComponent
                installCommand={installCommand}
                isLoadingInstallCommand={isLoadingInstallCommand}
              />
            </div>
          )}
        </ModalBody>
        <ModalFooter className='mt-6'>
          <Button
            variant='outlined'
            onClick={handleCloseModal}
            aria-label={modalMode === 'configure' ? 'Cancel configure' : 'Cancel'}
            className='select-none'
          >
            Cancel
          </Button>
          {currentStep === 1 && (modalMode === 'create' || modalMode === 'edit') && (
            <Button
              onClick={handleNextClick}
              aria-label={modalMode === 'edit' ? 'Save runner button' : 'Create runner button'}
              className='select-none'
            >
              {modalMode === 'edit' ? 'Done' : 'Create Runner'}
            </Button>
          )}
          {(currentStep === 2 || modalMode === 'configure') && (
            <Button
              onClick={handleComplete}
              disabled={isLoadingInstallCommand || !installCommand}
              aria-label='Configuration done button'
              className='select-none'
            >
              Done
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default RunnerSettingsTab
